<!-- ── Loading ──────────────────────────────────────────────────────── -->
<div *ngIf="isPageLoading" class="ptl-loading">
  <mat-spinner diameter="40"></mat-spinner>
</div>

<!-- ── No classId guard ──────────────────────────────────────────────── -->
<div *ngIf="!isPageLoading && !classSummary" class="ptl-error">
  <mat-icon>warning</mat-icon>
  <p>No class information available. Please navigate here from a class notification.</p>
  <button mat-stroked-button (click)="router.navigate(['/dashboard'])">Back to Dashboard</button>
</div>

<!-- ── Page ─────────────────────────────────────────────────────────── -->
<div *ngIf="!isPageLoading && classSummary" class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" (click)="router.navigate(['/dashboard'])"
            matTooltip="Back to Dashboard">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">Policy Template Linking</h2>
    <span class="ptl-class-chip">{{ classSummary.name }}</span>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="false" #stepper>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Class Info (read-only)                               -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="readonlyForm">
        <ng-template matStepLabel>
          <span matTooltip="Class details this policy will be applied to" matTooltipPosition="below">
            Class Info
          </span>
        </ng-template>

        <form [formGroup]="readonlyForm" class="step-form">
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Class Name</mat-label>
            <mat-icon matPrefix>badge</mat-icon>
            <input matInput [value]="classSummary.name || '—'" readonly>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Course</mat-label>
            <mat-icon matPrefix>school</mat-icon>
            <input matInput [value]="courseName" readonly>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Course Code</mat-label>
            <mat-icon matPrefix>tag</mat-icon>
            <input matInput [value]="courseCode" readonly>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Status</mat-label>
            <mat-icon matPrefix>flag</mat-icon>
            <input matInput [value]="classSummary.status || '—'" readonly>
          </mat-form-field>
        </form>

        <div class="step-actions">
          <span></span>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — Schedule (read-only)                                 -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="readonlyForm">
        <ng-template matStepLabel>
          <span matTooltip="Training schedule and session dates for this class" matTooltipPosition="below">
            Schedule
          </span>
        </ng-template>

        <form [formGroup]="readonlyForm" class="step-form">
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Schedule Title</mat-label>
            <mat-icon matPrefix>calendar_month</mat-icon>
            <input matInput [value]="classSummary.trainingSchedule?.title || '—'" readonly>
          </mat-form-field>

          <div class="date-ranges-section">
            <div class="section-sub-heading-row">
              <span class="section-sub-heading">Sessions</span>
            </div>

            <ng-container *ngIf="(classSummary.trainingSchedule?.dateRanges?.length ?? 0) > 0; else noDateRanges">
              <div class="ptl-date-list">
                <div *ngFor="let dr of classSummary.trainingSchedule!.dateRanges; let i = index"
                     class="ptl-date-row">
                  <mat-icon>calendar_today</mat-icon>
                  <span>Session {{ i + 1 }}:&nbsp;
                    {{ dr?.startDate ?? dr?.StartDate | date:'d MMM yyyy' }} —
                    {{ dr?.endDate   ?? dr?.EndDate   | date:'d MMM yyyy' }}
                  </span>
                </div>
              </div>
            </ng-container>
            <ng-template #noDateRanges>
              <p class="trainer-hint">No session dates recorded.</p>
            </ng-template>
          </div>
        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Assigned Templates (read-only)                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="readonlyForm">
        <ng-template matStepLabel>
          <span matTooltip="VM and User Account templates assigned to this class" matTooltipPosition="below">
            Assigned Templates
          </span>
        </ng-template>

        <div class="step-form">

          <ng-container *ngIf="hasVm">
            <p class="ptl-section-label">
              <mat-icon>computer</mat-icon>VM Templates
            </p>
            <ng-container *ngFor="let e of vmResourceEntries">
              <mat-accordion class="vm-preview-accordion">
                <mat-expansion-panel class="vm-preview-panel" [expanded]="false">
                  <mat-expansion-panel-header>
                    <mat-panel-title class="vp-panel-title">
                      <mat-icon class="vp-panel-icon">description</mat-icon>
                      {{ e.name || e.id }}
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="vp-grid">
                    <span class="vp-label"><mat-icon class="vp-label-icon">label</mat-icon>Name</span>
                    <span class="vp-value">{{ e.name || '—' }}</span>
                    <span class="vp-label"><mat-icon class="vp-label-icon">category</mat-icon>Type</span>
                    <span class="vp-value">Virtual Machine</span>
                    <span class="vp-label"><mat-icon class="vp-label-icon">fingerprint</mat-icon>Template ID</span>
                    <span class="vp-value">{{ e.id }}</span>
                  </div>
                </mat-expansion-panel>
              </mat-accordion>
            </ng-container>
          </ng-container>

          <ng-container *ngIf="hasUa">
            <p class="ptl-section-label" [class.ptl-section-label--spaced]="hasVm">
              <mat-icon>manage_accounts</mat-icon>User Account Templates
            </p>
            <ng-container *ngFor="let e of uaResourceEntries">
              <mat-accordion class="vm-preview-accordion">
                <mat-expansion-panel class="vm-preview-panel" [expanded]="false">
                  <mat-expansion-panel-header>
                    <mat-panel-title class="vp-panel-title">
                      <mat-icon class="vp-panel-icon">description</mat-icon>
                      {{ e.name || e.id }}
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="vp-grid">
                    <span class="vp-label"><mat-icon class="vp-label-icon">label</mat-icon>Name</span>
                    <span class="vp-value">{{ e.name || '—' }}</span>
                    <span class="vp-label"><mat-icon class="vp-label-icon">category</mat-icon>Type</span>
                    <span class="vp-value">User Account</span>
                    <span class="vp-label"><mat-icon class="vp-label-icon">fingerprint</mat-icon>Template ID</span>
                    <span class="vp-value">{{ e.id }}</span>
                  </div>
                </mat-expansion-panel>
              </mat-accordion>
            </ng-container>
          </ng-container>

          <ng-container *ngIf="!hasVm && !hasUa">
            <p class="trainer-hint">No resource templates are assigned to this class.</p>
          </ng-container>

        </div>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 4 — Policy Selection (editable)                         -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="policyForm">
        <ng-template matStepLabel>
          <span matTooltip="Select the policy templates to govern VM and User Account lifecycle for this class"
                matTooltipPosition="below">
            Policy Selection
          </span>
        </ng-template>

        <form [formGroup]="policyForm" class="step-form">

          <ng-container *ngIf="!hasVm && !hasUa">
            <div class="ptl-warn-box">
              <mat-icon>warning</mat-icon>
              <span>This class has no VM or User Account resource templates assigned.
                    A policy can only be linked when at least one template type is present.</span>
            </div>
          </ng-container>

          <ng-container *ngIf="hasVm">
            <p class="ptl-section-label">
              <mat-icon>computer</mat-icon>VM Policy Template
            </p>
            <mat-form-field appearance="outline" class="form-field-full">
              <mat-label>Select VM Policy</mat-label>
              <mat-icon matPrefix>policy</mat-icon>
              <mat-select formControlName="vmPolicyId" required>
                <mat-option value="">— None —</mat-option>
                <mat-option *ngFor="let p of vmPolicies" [value]="p.id">
                  {{ p.policyName }}
                </mat-option>
              </mat-select>
              <mat-error>A VM policy template is required.</mat-error>
            </mat-form-field>
            <div class="ptl-view-row" *ngIf="selectedVmPolicy">
              <button mat-stroked-button type="button" (click)="openDrawer(selectedVmPolicy!)">
                <mat-icon>open_in_new</mat-icon>View Details
              </button>
            </div>
          </ng-container>

          <ng-container *ngIf="hasUa">
            <p class="ptl-section-label" [class.ptl-section-label--spaced]="hasVm">
              <mat-icon>manage_accounts</mat-icon>User Account Policy Template
            </p>
            <mat-form-field appearance="outline" class="form-field-full">
              <mat-label>Select User Account Policy</mat-label>
              <mat-icon matPrefix>policy</mat-icon>
              <mat-select formControlName="uaPolicyId" required>
                <mat-option value="">— None —</mat-option>
                <mat-option *ngFor="let p of uaPolicies" [value]="p.id">
                  {{ p.policyName }}
                </mat-option>
              </mat-select>
              <mat-error>A User Account policy template is required.</mat-error>
            </mat-form-field>
            <div class="ptl-view-row" *ngIf="selectedUaPolicy">
              <button mat-stroked-button type="button" (click)="openDrawer(selectedUaPolicy!)">
                <mat-icon>open_in_new</mat-icon>View Details
              </button>
            </div>
          </ng-container>

        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext
                  [disabled]="!isPolicyStepValid">
            Review
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 5 — Review & Link                                        -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="reviewForm">
        <ng-template matStepLabel>
          <span matTooltip="Verify the policy assignments and confirm linking" matTooltipPosition="below">
            Review &amp; Link
          </span>
        </ng-template>

        <mat-accordion class="review-accordion" [multi]="false">

          <!-- Panel A: Class Info -->
          <mat-expansion-panel #panelA class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">school</mat-icon>
                Class Info
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelA.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item">
                    <mat-icon class="rps-icon">badge</mat-icon>{{ classSummary.name }}
                  </span>
                  <span class="rps-item" *ngIf="courseName !== '—'">
                    <mat-icon class="rps-icon">school</mat-icon>{{ courseName }}
                  </span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">badge</mat-icon>Class Name</span>
              <span class="review-value">{{ classSummary.name || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">school</mat-icon>Course</span>
              <span class="review-value">{{ courseName }}</span>
              <span class="review-label"><mat-icon class="rl-icon">tag</mat-icon>Course Code</span>
              <span class="review-value">{{ courseCode }}</span>
              <span class="review-label"><mat-icon class="rl-icon">flag</mat-icon>Status</span>
              <span class="review-value">{{ classSummary.status || '—' }}</span>
            </div>
          </mat-expansion-panel>

          <!-- Panel B: Schedule -->
          <mat-expansion-panel #panelB class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">calendar_month</mat-icon>
                Schedule
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelB.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="classSummary.trainingSchedule?.title">
                    <mat-icon class="rps-icon">label</mat-icon>{{ classSummary.trainingSchedule!.title }}
                  </span>
                  <span class="rps-item">
                    <mat-icon class="rps-icon">calendar_today</mat-icon>
                    {{ (classSummary.trainingSchedule?.dateRanges?.length ?? 0) }} session(s)
                  </span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">label</mat-icon>Title</span>
              <span class="review-value" style="grid-column: span 3">
                {{ classSummary.trainingSchedule?.title || '—' }}
              </span>
            </div>
            <div class="slot-review-list"
                 *ngIf="(classSummary.trainingSchedule?.dateRanges?.length ?? 0) > 0">
              <div *ngFor="let dr of classSummary.trainingSchedule!.dateRanges; let i = index"
                   class="slot-review-row">
                <mat-icon>event</mat-icon>
                <span>Session {{ i + 1 }}:
                  {{ dr?.startDate ?? dr?.StartDate | date:'d MMM' }} —
                  {{ dr?.endDate   ?? dr?.EndDate   | date:'d MMM yyyy' }}
                </span>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Panel C: Assigned Templates -->
          <mat-expansion-panel #panelC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">layers</mat-icon>
                Assigned Templates
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="hasVm">
                    <mat-icon class="rps-icon">computer</mat-icon>{{ vmResourceEntries.length }} VM
                  </span>
                  <span class="rps-item" *ngIf="hasUa">
                    <mat-icon class="rps-icon">manage_accounts</mat-icon>{{ uaResourceEntries.length }} UA
                  </span>
                  <span class="rps-item" *ngIf="!hasVm && !hasUa">None assigned</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid" *ngIf="hasVm">
              <span class="review-label" style="align-self: start">
                <mat-icon class="rl-icon">computer</mat-icon>VM
              </span>
              <div style="grid-column: span 3; display: flex; flex-wrap: wrap; gap: 6px; padding: 2px 0">
                <span *ngFor="let e of vmResourceEntries" class="ptl-rcard-chip">{{ e.name || e.id }}</span>
              </div>
            </div>
            <div class="review-grid" *ngIf="hasUa" [style.margin-top]="hasVm ? '8px' : '0'">
              <span class="review-label" style="align-self: start">
                <mat-icon class="rl-icon">manage_accounts</mat-icon>User Account
              </span>
              <div style="grid-column: span 3; display: flex; flex-wrap: wrap; gap: 6px; padding: 2px 0">
                <span *ngFor="let e of uaResourceEntries" class="ptl-rcard-chip">{{ e.name || e.id }}</span>
              </div>
            </div>
            <p class="trainer-hint" *ngIf="!hasVm && !hasUa">No templates assigned.</p>
          </mat-expansion-panel>

          <!-- Panel D: Policy Selection (expanded by default) -->
          <mat-expansion-panel #panelD class="review-panel" [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">policy</mat-icon>
                Policy Selection
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelD.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="selectedVmPolicy">
                    <mat-icon class="rps-icon">computer</mat-icon>{{ selectedVmPolicy.policyName }}
                  </span>
                  <span class="rps-item" *ngIf="selectedUaPolicy">
                    <mat-icon class="rps-icon">manage_accounts</mat-icon>{{ selectedUaPolicy.policyName }}
                  </span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <ng-container *ngIf="hasVm && selectedVmPolicy">
              <div class="review-grid">
                <span class="review-label"><mat-icon class="rl-icon">computer</mat-icon>VM Policy</span>
                <span class="review-value">{{ selectedVmPolicy.policyName }}</span>
                <span class="review-label"><mat-icon class="rl-icon">article</mat-icon>Description</span>
                <span class="review-value">{{ selectedVmPolicy.description || '—' }}</span>
              </div>
            </ng-container>
            <ng-container *ngIf="hasUa && selectedUaPolicy">
              <div class="review-grid" [style.margin-top]="hasVm && selectedVmPolicy ? '8px' : '0'">
                <span class="review-label"><mat-icon class="rl-icon">manage_accounts</mat-icon>UA Policy</span>
                <span class="review-value">{{ selectedUaPolicy.policyName }}</span>
                <span class="review-label"><mat-icon class="rl-icon">article</mat-icon>Description</span>
                <span class="review-value">{{ selectedUaPolicy.description || '—' }}</span>
              </div>
            </ng-container>
            <ng-container *ngIf="(!hasVm || !selectedVmPolicy) && (!hasUa || !selectedUaPolicy)">
              <p class="trainer-hint">No policies selected.</p>
            </ng-container>
          </mat-expansion-panel>

        </mat-accordion>

        <div class="step-actions">
          <button mat-button matStepperPrevious [disabled]="isSaving">Back</button>
          <button mat-flat-button color="primary"
                  [disabled]="!isPolicyStepValid || isSaving"
                  (click)="onSave()">
            <mat-spinner *ngIf="isSaving" diameter="18" class="btn-spinner"></mat-spinner>
            {{ isSaving ? 'Saving…' : 'Confirm & Link' }}
            <mat-icon iconPositionEnd *ngIf="!isSaving">link</mat-icon>
          </button>
        </div>
      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>

</div>

<!-- ── Policy detail drawer ──────────────────────────────────────────── -->
<app-duplicate-policy-drawer
  [policySummary]="drawerPolicy"
  (closed)="closeDrawer()">
</app-duplicate-policy-drawer>
